import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config";
import { MapPin, Phone, Search, Wrench } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface Shop {
    shop_id: number;
    shop_name: string;
    address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    phone: string | null;
    chain: { chain_name: string; logo_url: string | null } | null;
}

export default function RepairShopLocatorPage() {
    const [shops, setShops] = useState<Shop[]>([]);
    const [loading, setLoading] = useState(false);
    const [city, setCity] = useState("");
    const [state, setState] = useState("");

    const fetchShops = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (city)  params.set("city",  city);
            if (state) params.set("state", state);
            const res = await axios.get(`${API_URL}/repair-shops?${params}`, { withCredentials: true });
            setShops(res.data.shops ?? []);
        } catch {
            setShops([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchShops(); }, []);

    return (
        <div className="max-w-5xl mx-auto space-y-8 py-8 px-4">
            <div className="text-center">
                <Wrench className="h-12 w-12 mx-auto text-indigo-600 mb-3" />
                <h1 className="text-3xl font-bold">Find a Service Shop</h1>
                <p className="text-muted-foreground mt-2">
                    Search repair shops near you and book an appointment online.
                </p>
            </div>

            <div className="flex gap-3">
                <Input
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="flex-1"
                />
                <Input
                    placeholder="State (e.g. TX)"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-28"
                />
                <Button onClick={fetchShops} disabled={loading}>
                    <Search className="h-4 w-4 mr-2" />
                    Search
                </Button>
            </div>

            {loading && <p className="text-center text-muted-foreground">Loading shops…</p>}

            {!loading && shops.length === 0 && (
                <p className="text-center text-muted-foreground">No shops found. Try a different city or state.</p>
            )}

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {shops.map((shop) => (
                    <div key={shop.shop_id} className="bg-card rounded-xl border p-5 space-y-3 hover:shadow-md transition">
                        {shop.chain && (
                            <p className="text-xs font-medium text-indigo-600 uppercase tracking-wide">
                                {shop.chain.chain_name}
                            </p>
                        )}
                        <h3 className="font-semibold text-base">{shop.shop_name}</h3>
                        {(shop.city || shop.state) && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3.5 w-3.5" />
                                {[shop.address, shop.city, shop.state, shop.zip].filter(Boolean).join(", ")}
                            </p>
                        )}
                        {shop.phone && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                                <Phone className="h-3.5 w-3.5" />
                                {shop.phone}
                            </p>
                        )}
                        <Link
                            to={`/repair-shops/${shop.shop_id}`}
                            className="inline-block rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white font-medium hover:bg-indigo-700 transition"
                        >
                            View Shop
                        </Link>
                    </div>
                ))}
            </div>
        </div>
    );
}
